import type { BarTone } from "./HorizontalBarList";

export interface StackedSegment {
  label: string;
  value: number;
  tone: BarTone;
}

interface StackedBarProps {
  segments: StackedSegment[];
  /** Bar thickness in px. Default 8 for the compact in-row variant. */
  height?: number;
  showLegend?: boolean;
  emptyMessage?: string;
}

const FILL_CLASS: Record<BarTone, string> = {
  sky: "bg-info/70",
  emerald: "bg-success/70",
  amber: "bg-warning/70",
  red: "bg-danger/70",
  slate: "bg-neutral/70",
};

const LEGEND_DOT: Record<BarTone, string> = {
  sky: "bg-info/70",
  emerald: "bg-success/70",
  amber: "bg-warning/70",
  red: "bg-danger/70",
  slate: "bg-neutral/70",
};

export function StackedBar({
  segments,
  height = 8,
  showLegend = false,
  emptyMessage = "No data",
}: StackedBarProps) {
  const total = segments.reduce((acc, s) => acc + Math.max(0, s.value), 0);

  if (total === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle px-3 py-3 text-center text-xs text-fg-faint">
        {emptyMessage}
      </div>
    );
  }

  const positive = segments.filter((s) => s.value > 0);
  const ariaLabel = positive
    .map((s) => `${s.label}: ${s.value.toLocaleString("en-US")}`)
    .join(", ");

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div
        className="flex w-full overflow-hidden rounded-full bg-surface-inset/60"
        style={{ height }}
        role="img"
        aria-label={ariaLabel}
      >
        {positive.map((s) => (
          <span
            key={s.label}
            className={`h-full ${FILL_CLASS[s.tone]}`}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={`${s.label}: ${s.value.toLocaleString("en-US")}`}
          />
        ))}
      </div>

      {showLegend && (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {segments.map((s) => (
            <li key={s.label} className="flex items-center gap-1.5">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${LEGEND_DOT[s.tone]}`}
                aria-hidden="true"
              />
              <span className="text-neutral-text">{s.label}</span>
              <span className="tabular-nums text-neutral-text-strong">
                {s.value.toLocaleString("en-US")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
